import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, MapPin, DoorOpen, Archive } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, humanise } from '@/lib/format';
import {
  Badge, Button, Card, EmptyState, Field, Input, LoadingBlock, Modal, MoneyInput,
  PageHeader, Select, Textarea, useToast,
} from '@/components/ui';
import { DriveImage } from '@/components/Files';
import type { Property, PropertyType, LettingMode } from '@shared/types';

const TYPES: PropertyType[] = [
  'apartment_block', 'house', 'shared_house', 'studio_block',
  'commercial', 'mixed_use', 'land', 'other',
];

const COMMON_FACILITIES = [
  'Secure parking', 'CCTV', 'Backup generator', 'Borehole water', 'Lift',
  'Laundry room', 'Garden', 'Gym', 'Swimming pool', 'Security guard',
  'Fibre internet', 'Solar water heater', 'Playground', 'Waste collection',
];

export default function Properties() {
  const { currency, can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { data, loading, reload } = useApi<Property[]>('/properties', { archived: showArchived });

  const properties = data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Properties"
        subtitle={`${properties.length} propert${properties.length === 1 ? 'y' : 'ies'} in the portfolio`}
        action={
          <>
            <Button
              variant="ghost"
              size="sm"
              icon={<Archive className="h-4 w-4" />}
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
            {can('staff') && (
              <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                Add property
              </Button>
            )}
          </>
        }
      />

      {loading ? (
        <LoadingBlock rows={4} />
      ) : properties.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title="No properties yet"
            message="Add your first property to start tracking rooms, tenants and bills."
            action={can('staff') && (
              <Button variant="primary" onClick={() => setCreating(true)} icon={<Plus className="h-4 w-4" />}>
                Add property
              </Button>
            )}
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {properties.map((p) => (
            <Link
              key={p.id}
              to={`/app/properties/${p.id}`}
              className="card overflow-hidden hover:border-faint hover:-translate-y-0.5 transition-all group"
            >
              <div className="aspect-[16/9] bg-raised relative overflow-hidden">
                <DriveImage
                  fileId={null}
                  alt={p.name}
                  className="w-full h-full object-cover"
                  fallback={<Building2 className="h-8 w-8" />}
                />
                {p.is_archived && (
                  <div className="absolute top-3 left-3">
                    <Badge tone="neutral" className="bg-black/60 border-transparent text-white">Archived</Badge>
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <Badge tone={p.letting_mode === 'per_room' ? 'accent' : 'brand'}>
                    {p.letting_mode === 'per_room' ? 'Per room' : 'Whole property'}
                  </Badge>
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold tracking-tight truncate group-hover:text-brand transition-colors">
                      {p.name}
                    </h3>
                    <p className="text-xs text-faint mt-0.5">{humanise(p.type)}</p>
                  </div>
                  {p.code && <Badge tone="neutral">{p.code}</Badge>}
                </div>

                {(p.address_line1 || p.city) && (
                  <p className="flex items-center gap-1.5 text-sm text-muted mt-2.5 truncate">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {[p.address_line1, p.city].filter(Boolean).join(', ')}
                    </span>
                  </p>
                )}

                <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-line">
                  <div>
                    <p className="label">Monthly rent</p>
                    <p className="font-bold tnum mt-0.5">
                      {money(p.monthly_rent_cents ?? 0, currency)}
                    </p>
                  </div>
                  {p.letting_mode === 'per_room' && (
                    <div className="text-right">
                      <p className="label">Occupancy</p>
                      <p className="font-bold tnum mt-0.5 flex items-center gap-1.5 justify-end">
                        <DoorOpen className="h-3.5 w-3.5 text-faint" />
                        {p.occupied_count ?? 0}/{p.room_count ?? 0}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {creating && (
        <PropertyFormModal
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

export function PropertyFormModal({ property, onClose, onDone }: {
  property?: Property; onClose: () => void; onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: property?.name ?? '',
    code: property?.code ?? '',
    type: property?.type ?? ('apartment_block' as PropertyType),
    letting_mode: property?.letting_mode ?? ('per_room' as LettingMode),
    address_line1: property?.address_line1 ?? '',
    city: property?.city ?? '',
    state_region: property?.state_region ?? '',
    postal_code: property?.postal_code ?? '',
    country: property?.country ?? '',
    bedrooms: property?.bedrooms ?? '',
    bathrooms: property?.bathrooms ?? '',
    year_built: property?.year_built ?? '',
    notes: property?.notes ?? '',
  });
  const [rentCents, setRentCents] = useState(property?.rent_cents ?? 0);
  const [depositCents, setDepositCents] = useState(property?.deposit_cents ?? 0);
  const [facilities, setFacilities] = useState<string[]>(property?.facilities ?? []);
  const [customFacility, setCustomFacility] = useState('');

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  function toggleFacility(name: string) {
    setFacilities((list) =>
      list.includes(name) ? list.filter((f) => f !== name) : [...list, name],
    );
  }

  async function submit() {
    if (!form.name.trim()) { toast.error('Give the property a name'); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        bedrooms: form.bedrooms === '' ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === '' ? null : Number(form.bathrooms),
        year_built: form.year_built === '' ? null : Number(form.year_built),
        rent_cents: rentCents,
        deposit_cents: depositCents,
        facilities,
      };
      if (property) await api.patch(`/properties/${property.id}`, payload);
      else await api.post('/properties', payload);
      toast.success(property ? 'Property updated' : 'Property added');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the property');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={property ? 'Edit property' : 'Add a property'}
      subtitle="Rooms, utilities and photos are added once it exists"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>
            {property ? 'Save changes' : 'Add property'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Name" required className="sm:col-span-2">
            <Input value={form.name} onChange={set('name')} placeholder="Harbour View Apartments" autoFocus />
          </Field>
          <Field label="Code" hint="Short reference">
            <Input value={form.code} onChange={set('code')} placeholder="HVA" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Type">
            <Select value={form.type} onChange={set('type')}>
              {TYPES.map((t) => <option key={t} value={t}>{humanise(t)}</option>)}
            </Select>
          </Field>
          <Field
            label="How is it let?"
            hint={form.letting_mode === 'per_room'
              ? 'Each room has its own rent and tenant'
              : 'One tenant takes the whole property'}
          >
            <Select value={form.letting_mode} onChange={set('letting_mode')}>
              <option value="per_room">Room by room</option>
              <option value="whole_property">As a whole property</option>
            </Select>
          </Field>
        </div>

        {form.letting_mode === 'whole_property' && (
          <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-xl bg-raised border border-line">
            <Field label="Monthly rent">
              <MoneyInput valueCents={rentCents} onChangeCents={setRentCents} currency={currency} />
            </Field>
            <Field label="Deposit">
              <MoneyInput valueCents={depositCents} onChangeCents={setDepositCents} currency={currency} />
            </Field>
          </div>
        )}

        <div>
          <p className="label mb-2">Address</p>
          <div className="space-y-3">
            <Input value={form.address_line1} onChange={set('address_line1')} placeholder="Street address" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input value={form.city} onChange={set('city')} placeholder="City" />
              <Input value={form.state_region} onChange={set('state_region')} placeholder="State / region" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input value={form.postal_code} onChange={set('postal_code')} placeholder="Postal code" />
              <Input value={form.country} onChange={set('country')} placeholder="Country" />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Bedrooms">
            <Input type="number" min={0} value={form.bedrooms} onChange={set('bedrooms')} />
          </Field>
          <Field label="Bathrooms">
            <Input type="number" min={0} value={form.bathrooms} onChange={set('bathrooms')} />
          </Field>
          <Field label="Year built">
            <Input type="number" min={1500} max={2200} value={form.year_built} onChange={set('year_built')} />
          </Field>
        </div>

        <div>
          <p className="label mb-2">Facilities</p>
          <div className="flex flex-wrap gap-2">
            {[...new Set([...COMMON_FACILITIES, ...facilities])].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => toggleFacility(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  facilities.includes(f)
                    ? 'bg-brand/15 text-brand border-brand/30'
                    : 'bg-raised text-muted border-line hover:text-ink'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Input
              value={customFacility}
              onChange={(e) => setCustomFacility(e.target.value)}
              placeholder="Add another facility"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && customFacility.trim()) {
                  e.preventDefault();
                  toggleFacility(customFacility.trim());
                  setCustomFacility('');
                }
              }}
            />
            <Button
              onClick={() => {
                if (customFacility.trim()) {
                  toggleFacility(customFacility.trim());
                  setCustomFacility('');
                }
              }}
            >
              Add
            </Button>
          </div>
        </div>

        <Field label="Notes">
          <Textarea value={form.notes} onChange={set('notes')} placeholder="Anything worth remembering about this property" />
        </Field>
      </div>
    </Modal>
  );
}

export { COMMON_FACILITIES };
